import { useEffect, useMemo, useState } from "react";

import { DataTable, DataTableCellSelection } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import { KeyValue } from "../../ProviderProfile/models";
import httpService from "../../../services/httpService";
import { ApiEndpoints, Constants } from "../../../constants/appConstants";
import { useAuthContext } from "../../../Context/AuthContext";
import { useAppContext } from "../../../Context";



export interface RHProvidersKpiDashboardProps {
  providersData: any;
  searchHandler: (query: any, columns: any, exactMatch?: boolean) => void;
  reviewYear?: number;
}

export const RHKpiDashboardComponent = ({
  searchHandler,
  reviewYear: propReviewYear,
}: RHProvidersKpiDashboardProps) => {
  const [providersPerformanceData, setProvidersPerformanceData] = useState<
    Record<string, string>[]
  >([]);
  const [providersTiersData, setProvidersTiersData] = useState<
    Record<string, string>[]
  >([]);
  const [selectedCell, setSelectedCell] = useState<DataTableCellSelection<
    KeyValue[]
  > | null>(null);
  const [selectedTierCell, setSelectedTierCell] =
    useState<DataTableCellSelection<KeyValue[]> | null>(null);

  const [statusColumns, setStatusColumns] = useState<string[]>([]);
  const [tierColumnsState, setTierColumnsState] = useState<string[]>([]);
  const [loadingKpi, setLoadingKpi] = useState<boolean>(true);
  const [loadingTiers, setLoadingTiers] = useState<boolean>(true);
  const [dataError, setDataError] = useState<boolean>(false);
  const { reviewYear: contextReviewYear } = useAppContext();
  const reviewYear = propReviewYear ?? contextReviewYear;
  const { isAdministrator } = useAuthContext();
  const nonSelectableFields = ['PBC Type', 'Provider Review Status', 'Provider Tier Status'];
    useEffect(() => {
    if (reviewYear) {
      getRsKpiDashboardData();
    }
  }, [reviewYear]);
  
  const getRsKpiDashboardData = () => {
    setLoadingKpi(true);
    setLoadingTiers(true);
    setDataError(false);
    httpService.get<any>(`${ApiEndpoints.getRsKpiDashboardData}/${reviewYear}`).then((response) => {
      try {
        const responseData = response.data || {};
        let kpiData = responseData.kpiData || [];
        let tierData = responseData.tierData || [];

        // Validate data is array
        if (!Array.isArray(kpiData)) {
          console.warn('kpiData is not an array, converting:', kpiData);
          kpiData = Array.isArray(responseData) ? responseData : [];
        }
        if (!Array.isArray(tierData)) {
          console.warn('tierData is not an array:', tierData);
          tierData = [];
        }
        
        // If data is still empty/invalid, show error
        if (!Array.isArray(kpiData) || kpiData.length === 0 || !Array.isArray(tierData)) {
          console.warn('Invalid data received:', { kpiData, tierData });
          setDataError(true);
          setLoadingKpi(false);
          setLoadingTiers(false);
          return;
        }
        
        const excludedTierNames = Constants.ExcludedTiers || [];
        const tierDataWithTotals = tierData.map((row: Record<string, string>) => {
          const filteredRow: Record<string, string> = {};
          Object.keys(row).forEach(key => {
            if (key === 'PBC Type' || key === 'Total' || !excludedTierNames.includes(key.toUpperCase())) {
              filteredRow[key] = row[key];
            }
          });
          
          const keys = Object.keys(filteredRow).filter(k => k !== 'PBC Type' && k !== 'Total');
          
          const isPercentage = keys.some(k => filteredRow[k]?.includes('%'));
          
          filteredRow['PBC Type'] = isPercentage ? 'Percentage' : 'Count';
          
          if (!filteredRow.Total) {
            if (isPercentage) {
              const totalPercentage = keys.reduce((sum, key) => {
                const val = filteredRow[key]?.replace('%', '');
                const num = parseFloat(val);
                return sum + (isNaN(num) ? 0 : num);
              }, 0);
              filteredRow.Total = totalPercentage > 0 ? String(totalPercentage.toFixed(2)) + '%' : '0%';
            } else {
              const total = keys.reduce((sum, key) => {
                const val = filteredRow[key];
                const num = parseInt(val, 10);
                return sum + (isNaN(num) ? 0 : num);
              }, 0);
              filteredRow.Total = total > 0 ? String(total) : '0';
            }
          }
          
          return filteredRow;
        });
        
        // Process KPI data
        const dataWithTotal = kpiData.map((row: Record<string, string>) => {
          const keys = Object.keys(row).filter(k => k !== 'providerIndicator' && k !== 'PBC Type' && k !== 'Total');
          // Check if any value contains '%' to detect percentage row
          const isPercentage = keys.some(k => row[k]?.includes('%'));
          
          // Set row label to Count or Percentage
          row['PBC Type'] = isPercentage ? 'Percentage' : 'Count';
          
          // Calculate or preserve Total
          if (!row.Total) {
            if (isPercentage) {
              const totalPercentage = keys.reduce((sum, key) => {
                const val = row[key]?.replace('%', '');
                const num = parseFloat(val);
                return sum + (isNaN(num) ? 0 : num);
              }, 0);
              row.Total = totalPercentage > 0 ? String(totalPercentage.toFixed(2)) + '%' : '-';
            } else {
              const total = keys.reduce((sum, key) => {
                const val = row[key];
                const num = parseInt(val, 10);
                return sum + (isNaN(num) ? 0 : num);
              }, 0);
              row.Total = total > 0 ? String(total) : '-';
            }
          }
          
          return row;
        });

        // Extract column keys from first row (excluding PBC Type and Total)
        const cols = dataWithTotal.length > 0 
          ? Object.keys(dataWithTotal[0]).filter(k => k !== 'PBC Type' && k !== 'Total' && k !== 'providerIndicator')
          : [];
        
        // Extract tier columns
        const tierCols = tierDataWithTotals.length > 0
          ? Object.keys(tierDataWithTotals[0]).filter(k => k !== 'PBC Type' && k !== 'Total')
          : [];
        
        setStatusColumns(cols);
        setTierColumnsState(tierCols);
        setProvidersPerformanceData(dataWithTotal);
        setProvidersTiersData(tierDataWithTotals);
        setDataError(false);
        setLoadingKpi(false);
        setLoadingTiers(false);
      } catch (error) {
        console.error('Error processing KPI and tier data:', error);
        setDataError(true);
        setLoadingKpi(false);
        setLoadingTiers(false);
      }
    }).catch((error) => {
      console.error('Error fetching combined KPI and tier data:', error);
      setDataError(true);
      setLoadingKpi(false);
      setLoadingTiers(false);
    });
  }

  const skeletonBodyTemplate = () => (
    <Skeleton width="80%" height="1.2rem" style={{ backgroundColor: '#e9ecef', borderRadius: '4px' }} />
  );

  const skeletonKpiRows = useMemo(() => [
    { 'PBC Type': '', ...Object.fromEntries(statusColumns.length > 0 ? statusColumns.map(c => [c, '']) : [['col1', ''], ['col2', ''], ['col3', '']]), Total: '' },
    { 'PBC Type': '', ...Object.fromEntries(statusColumns.length > 0 ? statusColumns.map(c => [c, '']) : [['col1', ''], ['col2', ''], ['col3', '']]), Total: '' },
  ], [statusColumns]);

  const skeletonKpiColumns = statusColumns.length > 0 ? statusColumns : ['col1', 'col2', 'col3'];

  const skeletonTierRows = useMemo(() => [
    { 'PBC Type': '', ...Object.fromEntries(tierColumnsState.length > 0 ? tierColumnsState.map(c => [c, '']) : [['col1', ''], ['col2', ''], ['col3', '']]), Total: '' },
    { 'PBC Type': '', ...Object.fromEntries(tierColumnsState.length > 0 ? tierColumnsState.map(c => [c, '']) : [['col1', ''], ['col2', ''], ['col3', '']]), Total: '' },
  ], [tierColumnsState]);

  const skeletonTierColumns = tierColumnsState.length > 0 ? tierColumnsState : ['col1', 'col2', 'col3'];

  const handleCellSelection = (e: any) => {
    if(!isAdministrator()){
      return;
    }
    const header = e.value?.column?.props?.header;
    // Ignore clicks on non-selectable columns
    if (header === "Provider Review Status" || header === "PBC Type") {
      return;
    }
    if (e.value !== null) {
      setSelectedCell(e.value);
      // If Total is clicked, show all records by passing empty string
      const searchValue = header === "Total" ? "" : header;
      searchHandler(searchValue, ['reviewStatus'], true);
    } else {
      setSelectedCell(null);
      searchHandler("", ['reviewStatus'], true);
    }
    setSelectedTierCell(null);
  };
  const handleTierCellSelection = (e: any) => {
    if(!isAdministrator()){
      return;
    }
    const header = e.value?.column?.props?.header;
    // Ignore clicks on non-selectable columns
    if (header === "Provider Tier Status" || header === "PBC Type") {
      return;
    }
    if (e.value !== null) {
      // If Total is clicked, show all records by passing empty string
      const searchValue = header === "Total" ? "" : header;
      searchHandler(searchValue, ['currentTier'], true);
      setSelectedTierCell(e.value);
    } else {
      setSelectedTierCell(null);
      searchHandler("", ['currentTier'], true);
    }
    setSelectedCell(null);
  };

  return (
    <>
      <div className="kpi-table-container">
      <DataTable
        value={loadingKpi ? skeletonKpiRows : providersPerformanceData}
        cellSelection={isAdministrator()}
        selectionMode={isAdministrator() ? "single" : undefined}
        selection={selectedCell}
        onSelectionChange={(e: any) => handleCellSelection(e)}
        size="small"
        className="kpi-table"
        emptyMessage={dataError ? "Error loading data. Please try again later." : "No available options"}
        isDataSelectable={(event) => {
          const field = (event as any).field;
          return !nonSelectableFields.includes(field);
        }}
      >
        <Column
          field="PBC Type"
          header="Provider Review Status"
          className="column-strong"
          headerClassName="kpi-table-header column-align-left"
          style={{ width: '180px', minWidth: '180px' }}
          body={loadingKpi ? skeletonBodyTemplate : undefined}
        ></Column>
        {(loadingKpi ? skeletonKpiColumns : statusColumns).map((col: string) =>
          <Column
            key={col}
            field={col}
            header={loadingKpi ? '' : col}
            headerClassName="kpi-table-header"
            bodyClassName="column-align-center"
            style={{ width: '120px', minWidth: '120px' }}
            body={loadingKpi ? skeletonBodyTemplate : undefined}
          ></Column>
        )}
        <Column
          field="Total"
          header={loadingKpi ? '' : 'Total'}
          className="column-strong"
          headerClassName="kpi-table-header"
          bodyClassName="column-align-center"
          style={{ width: '120px', minWidth: '120px' }}
          body={loadingKpi ? skeletonBodyTemplate : undefined}
        ></Column>
      </DataTable>

    </div>
    <div className="kpi-table-container">
      <DataTable
        value={(loadingKpi || loadingTiers) ? skeletonTierRows : providersTiersData}
        cellSelection={isAdministrator()}
        selectionMode={isAdministrator() ? "single" : undefined}
        selection={selectedTierCell}
        onSelectionChange={(e: any) => handleTierCellSelection(e)}
        size="small"
        className="kpi-table"
        emptyMessage={dataError ? "Error loading data. Please try again later." : "No available options"}
        isDataSelectable={(event) => {
          const field = (event as any).field;
          return !nonSelectableFields.includes(field);
        }}
      >
        <Column
          field="PBC Type"
          header="Provider Tier Status"
          className="column-strong"
          headerClassName="kpi-table-header column-align-left"
          style={{ width: '180px', minWidth: '180px' }}
          body={(loadingKpi || loadingTiers) ? skeletonBodyTemplate : undefined}
        ></Column>
        {((loadingKpi || loadingTiers) ? skeletonTierColumns : tierColumnsState).map((col: string) =>
          <Column
            key={col}
            field={col}
            header={(loadingKpi || loadingTiers) ? '' : col}
            headerClassName="kpi-table-header"
            bodyClassName="column-align-center"
            style={{ width: '120px', minWidth: '120px' }}
            body={(loadingKpi || loadingTiers) ? skeletonBodyTemplate : undefined}
          ></Column>
        )}
        <Column
          field="Total"
          header={(loadingKpi || loadingTiers) ? '' : 'Total'}
          className="column-strong"
          headerClassName="kpi-table-header"
          bodyClassName="column-align-center"
          style={{ width: '120px', minWidth: '120px' }}
          body={(loadingKpi || loadingTiers) ? skeletonBodyTemplate : undefined}
        ></Column>
      </DataTable>
    </div>
    </>
  );
};
